import React, { useEffect, useState, useCallback } from 'react';
const COLOR_PALETTE = [
  '#e8c97a', '#7ab8e8', '#7ae8b8', '#e87ab8',
  '#e8a87a', '#a87ae8', '#e87a7a', '#7ae8e8',
  '#b8e87a', '#e8e87a', '#7a9ae8', '#e87aa8',
];

import {
  fetchSavedGrocery, addSavedGroceryItem, deleteSavedGroceryItem,
  fetchSavedMeals, addSavedMeal, deleteSavedMeal,
  fetchAllPets, createPet, updatePet, deletePet,
  addPetMed, updatePetMed, deletePetMed,
  fetchRoster, addRosterMember, updateRosterMember, deleteRosterMember,
  fetchChores, createChore, updateChore, deleteChore,
} from '../api';

// ── Pets section ──────────────────────────────────────────────────────────────

function PetRow({ pet, onUpdate, onDelete, onAddMed, onUpdateMed, onDeleteMed }) {
  const [editing, setEditing] = useState(false);
  const [name, setName]       = useState(pet.name);
  const [scoops, setScoops]   = useState(String(pet.target_scoops));
  const [isDog, setIsDog]     = useState(pet.is_dog);
  const [medName, setMedName] = useState('');
  const [medQty, setMedQty]   = useState('');
  const [editMed, setEditMed] = useState(null); // {id, med_name, quantity}

  const save = async () => {
    await onUpdate(pet.id, { name, target_scoops: parseFloat(scoops) || 1, is_dog: isDog });
    setEditing(false);
  };

  const handleAddMed = async () => {
    if (!medName.trim() || !medQty.trim()) return;
    await onAddMed(pet.id, medName.trim(), medQty.trim());
    setMedName(''); setMedQty('');
  };

  const handleSaveMed = async () => {
    if (!editMed) return;
    await onUpdateMed(pet.id, editMed.id, editMed.med_name, editMed.quantity);
    setEditMed(null);
  };

  return (
    <div style={ps.petRow}>
      {/* Pet header */}
      <div style={ps.petHeader}>
        {editing ? (
          <div style={ps.editFields}>
            <input
              style={ps.input}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Pet name"
            />
            <input
              style={{...ps.input, width: 70}}
              value={scoops}
              onChange={e => setScoops(e.target.value)}
              placeholder="Scoops"
              type="number"
              step="0.5"
              min="0.5"
            />
            <label style={ps.dogLabel}>
              <input
                type="checkbox"
                checked={isDog}
                onChange={e => setIsDog(e.target.checked)}
                style={{accentColor: 'var(--accent)'}}
              />
              <span style={ps.dogText}>Dog</span>
            </label>
            <button style={ps.saveBtn} onClick={save}>Save</button>
            <button style={ps.cancelBtn} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        ) : (
          <div style={ps.petInfo}>
            <span style={ps.petName}>{pet.name}</span>
            <span style={ps.petMeta}>
              {pet.target_scoops % 1 === 0 ? pet.target_scoops : pet.target_scoops.toFixed(1)} scoops
              {pet.is_dog ? ' · 🐕 Dog' : ' · 🐱 Cat'}
            </span>
            <div style={ps.petActions}>
              <button style={ps.editBtn} onClick={() => setEditing(true)}>Edit</button>
              <button style={ps.deleteBtn} onClick={() => onDelete(pet.id)}>Remove</button>
            </div>
          </div>
        )}
      </div>

      {/* Meds */}
      <div style={ps.medsSection}>
        <span style={ps.medsLabel}>Medications</span>
        {pet.meds.map(med => (
          <div key={med.id} style={ps.medRow}>
            {editMed?.id === med.id ? (
              <>
                <input
                  style={ps.input}
                  value={editMed.med_name}
                  onChange={e => setEditMed(m => ({...m, med_name: e.target.value}))}
                  placeholder="Med name"
                />
                <input
                  style={{...ps.input, width: 80}}
                  value={editMed.quantity}
                  onChange={e => setEditMed(m => ({...m, quantity: e.target.value}))}
                  placeholder="Dosage"
                />
                <button style={ps.saveBtn} onClick={handleSaveMed}>Save</button>
                <button style={ps.cancelBtn} onClick={() => setEditMed(null)}>Cancel</button>
              </>
            ) : (
              <>
                <span style={ps.medName}>{med.med_name}</span>
                <span style={ps.medQty}>Dosage: {med.quantity}</span>
                <button style={ps.editBtn} onClick={() => setEditMed({...med})}>Edit</button>
                <button style={ps.deleteBtn} onClick={() => onDeleteMed(pet.id, med.id)}>✕</button>
              </>
            )}
          </div>
        ))}

        {/* Add med row */}
        <div style={ps.addMedRow}>
          <input
            style={ps.input}
            value={medName}
            onChange={e => setMedName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddMed()}
            placeholder="Med name"
          />
          <input
            style={{...ps.input, width: 80}}
            value={medQty}
            onChange={e => setMedQty(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddMed()}
            placeholder="Dosage (e.g. 2, ½ tablet)"
          />
          <button style={ps.addMedBtn} onClick={handleAddMed}>+ Add</button>
        </div>
      </div>
    </div>
  );
}

// ── Main ManageScreen ─────────────────────────────────────────────────────────

export default function ManageScreen({ onClose }) {
  const [groceryItems, setGroceryItems] = useState([]);
  const [mealItems, setMealItems]       = useState([]);
  const [pets, setPets]                 = useState([]);
  const [groceryInput, setGroceryInput] = useState('');
  const [mealInput, setMealInput]       = useState('');
  const [groceryError, setGroceryError] = useState('');
  const [mealError, setMealError]       = useState('');
  const [newPetName, setNewPetName]     = useState('');
  const [newPetScoops, setNewPetScoops] = useState('1');
  const [newPetDog, setNewPetDog]       = useState(false);
  const [petError, setPetError]         = useState('');
  const [roster, setRoster]               = useState([]);
  const [newMemberName, setNewMemberName]  = useState('');
  const [newMemberColor, setNewMemberColor] = useState(COLOR_PALETTE[0]);
  const [rosterError, setRosterError]      = useState('');
  const [chores, setChores]                = useState([]);
  const [newChoreName, setNewChoreName]    = useState('');
  const [newChoreRoom, setNewChoreRoom]    = useState('Kitchen');
  const [newChoreFreq, setNewChoreFreq]    = useState('weekly');
  const [newChoreAssign, setNewChoreAssign] = useState('');
  const [choreError, setChoreError]        = useState('');
  const [activeSection, setActiveSection]  = useState('grocery');

  const load = useCallback(async () => {
    const [g, m, p, r, ch] = await Promise.all([fetchSavedGrocery(), fetchSavedMeals(), fetchAllPets(), fetchRoster(), fetchChores()]);
    setGroceryItems(g);
    setMealItems(m);
    setPets(p);
    setRoster(r);
    setChores(ch);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Grocery
  const handleAddGrocery = async () => {
    if (!groceryInput.trim()) return;
    try {
      const item = await addSavedGroceryItem(groceryInput.trim());
      setGroceryItems(prev => [...prev, item].sort((a,b) => a.name.localeCompare(b.name)));
      setGroceryInput(''); setGroceryError('');
    } catch { setGroceryError('Already exists or could not add'); }
  };

  // Meals
  const handleAddMeal = async () => {
    if (!mealInput.trim()) return;
    try {
      const item = await addSavedMeal(mealInput.trim());
      setMealItems(prev => [...prev, item].sort((a,b) => a.name.localeCompare(b.name)));
      setMealInput(''); setMealError('');
    } catch { setMealError('Already exists or could not add'); }
  };

  // Pets
  const handleAddPet = async () => {
    if (!newPetName.trim()) return;
    try {
      const pet = await createPet(newPetName.trim(), parseFloat(newPetScoops) || 1, newPetDog);
      setPets(prev => [...prev, {...pet, meds: []}]);
      setNewPetName(''); setNewPetScoops('1'); setNewPetDog(false); setPetError('');
    } catch { setPetError('Name already exists or could not add'); }
  };

  const handleUpdatePet = async (id, fields) => {
    const updated = await updatePet(id, fields);
    setPets(prev => prev.map(p => p.id === id ? {...p, ...updated} : p));
  };

  const handleDeletePet = async (id) => {
    if (!window.confirm('Remove this pet and all their data?')) return;
    await deletePet(id);
    setPets(prev => prev.filter(p => p.id !== id));
  };

  const handleAddMed = async (petId, med_name, quantity) => {
    const med = await addPetMed(petId, med_name, quantity);
    setPets(prev => prev.map(p => p.id === petId ? {...p, meds: [...p.meds, med]} : p));
  };

  const handleUpdateMed = async (petId, medId, med_name, quantity) => {
    await updatePetMed(petId, medId, med_name, quantity);
    setPets(prev => prev.map(p => {
      if (p.id !== petId) return p;
      return {...p, meds: p.meds.map(m => m.id === medId ? {...m, med_name, quantity} : m)};
    }));
  };

  const handleDeleteMed = async (petId, medId) => {
    await deletePetMed(petId, medId);
    setPets(prev => prev.map(p => {
      if (p.id !== petId) return p;
      return {...p, meds: p.meds.filter(m => m.id !== medId)};
    }));
  };

  const handleAddMember = async () => {
    if (!newMemberName.trim()) return;
    try {
      const member = await addRosterMember(newMemberName.trim(), newMemberColor, roster.length === 0);
      setRoster(prev => [...prev, member]);
      setNewMemberName(''); setRosterError('');
    } catch { setRosterError('Name already exists'); }
  };

  const handleDeleteMember = async (id) => {
    await deleteRosterMember(id);
    setRoster(prev => prev.filter(m => m.id !== id));
  };

  const handleSetDefault = async (id) => {
    await updateRosterMember(id, { name: roster.find(m=>m.id===id)?.name, is_default: true });
    setRoster(prev => prev.map(m => ({...m, is_default: m.id === id ? 1 : 0})));
  };

  const SECTIONS = [
    { id: 'grocery', label: 'Grocery' },
    { id: 'meals',   label: 'Meals'   },
    { id: 'pets',    label: 'Pets'    },
    { id: 'roster',  label: 'Roster'  },
    { id: 'chores',  label: 'Chores'  },
  ];

  const ROOMS = ['Kitchen','Dining Room','Living Room','Upstairs Bathroom','Downstairs Bathroom'];

  const handleAddChore = async () => {
    if (!newChoreName.trim()) return;
    try {
      const chore = await createChore(newChoreName.trim(), newChoreRoom, newChoreFreq, newChoreAssign || null);
      setChores(prev => [...prev, chore]);
      setNewChoreName(''); setChoreError('');
    } catch { setChoreError('Could not add chore'); }
  };

  const handleDeleteChore = async (id) => {
    await deleteChore(id);
    setChores(prev => prev.filter(c => c.id !== id));
  };

  const handleUpdateChoreAssign = async (id, assigned_to) => {
    await updateChore(id, { assigned_to: assigned_to || null });
    const member = roster.find(r => r.id === parseInt(assigned_to));
    setChores(prev => prev.map(c => c.id === id ? {...c, assigned_to, assignee_name: member?.name, assignee_color: member?.color} : c));
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.sheet}>
        <div style={styles.sheetHeader}>
          <span style={styles.sheetTitle}>Manage</span>
          <div style={styles.sectionTabs}>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                style={{...styles.sectionTab, ...(activeSection === s.id ? styles.sectionTabActive : {})}}
                onClick={() => setActiveSection(s.id)}
              >{s.label}</button>
            ))}
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>

          {/* Grocery Favorites */}
          {activeSection === 'grocery' && (
            <section style={styles.section}>
              <div style={styles.addRow}>
                <input
                  style={styles.input}
                  value={groceryInput}
                  onChange={e => { setGroceryInput(e.target.value); setGroceryError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAddGrocery()}
                  placeholder="Add item (e.g. Milk, Eggs)"
                />
                <button style={styles.addBtn} onClick={handleAddGrocery}>Add</button>
              </div>
              {groceryError && <p style={styles.error}>{groceryError}</p>}
              <div style={styles.chipGrid}>
                {groceryItems.map(item => (
                  <div key={item.id} style={styles.chip}>
                    <span style={styles.chipName}>{item.name}</span>
                    <button style={styles.chipDelete} onClick={() => {
                      deleteSavedGroceryItem(item.id);
                      setGroceryItems(prev => prev.filter(i => i.id !== item.id));
                    }}>×</button>
                  </div>
                ))}
                {groceryItems.length === 0 && <p style={styles.empty}>No saved items yet</p>}
              </div>
            </section>
          )}

          {/* Meal Favorites */}
          {activeSection === 'meals' && (
            <section style={styles.section}>
              <div style={styles.addRow}>
                <input
                  style={styles.input}
                  value={mealInput}
                  onChange={e => { setMealInput(e.target.value); setMealError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAddMeal()}
                  placeholder="Add meal (e.g. Tacos, Pasta)"
                />
                <button style={{...styles.addBtn, background: 'var(--accent3)'}} onClick={handleAddMeal}>Add</button>
              </div>
              {mealError && <p style={styles.error}>{mealError}</p>}
              <div style={styles.chipGrid}>
                {mealItems.map(item => (
                  <div key={item.id} style={{...styles.chip, borderColor: 'rgba(122,232,184,0.2)'}}>
                    <span style={styles.chipName}>{item.name}</span>
                    <button style={styles.chipDelete} onClick={() => {
                      deleteSavedMeal(item.id);
                      setMealItems(prev => prev.filter(i => i.id !== item.id));
                    }}>×</button>
                  </div>
                ))}
                {mealItems.length === 0 && <p style={styles.empty}>No saved meals yet</p>}
              </div>
            </section>
          )}

          {/* Pets */}
          {activeSection === 'pets' && (
            <section style={styles.section}>
              {/* Add new pet */}
              <div style={ps.addPetRow}>
                <input
                  style={styles.input}
                  value={newPetName}
                  onChange={e => setNewPetName(e.target.value)}
                  placeholder="Pet name"
                  onKeyDown={e => e.key === 'Enter' && handleAddPet()}
                />
                <input
                  style={{...styles.input, width: 80}}
                  value={newPetScoops}
                  onChange={e => setNewPetScoops(e.target.value)}
                  placeholder="Scoops"
                  type="number"
                  step="0.5"
                  min="0.5"
                />
                <label style={ps.dogLabel}>
                  <input
                    type="checkbox"
                    checked={newPetDog}
                    onChange={e => setNewPetDog(e.target.checked)}
                    style={{accentColor: 'var(--accent)'}}
                  />
                  <span style={ps.dogText}>Dog</span>
                </label>
                <button style={{...styles.addBtn, background: '#e87ab8', flexShrink: 0}} onClick={handleAddPet}>
                  + Add Pet
                </button>
              </div>
              {petError && <p style={styles.error}>{petError}</p>}

              <div style={ps.petList}>
                {pets.map(pet => (
                  <PetRow
                    key={pet.id}
                    pet={pet}
                    onUpdate={handleUpdatePet}
                    onDelete={handleDeletePet}
                    onAddMed={handleAddMed}
                    onUpdateMed={handleUpdateMed}
                    onDeleteMed={handleDeleteMed}
                  />
                ))}
                {pets.length === 0 && <p style={styles.empty}>No pets yet</p>}
              </div>
            </section>
          )}

          {/* Chores */}
          {activeSection === 'chores' && (
            <section style={styles.section}>
              {/* Add new chore */}
              <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                <input
                  style={styles.input}
                  value={newChoreName}
                  onChange={e => { setNewChoreName(e.target.value); setChoreError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAddChore()}
                  placeholder="Chore name"
                />
                <select
                  style={{...styles.input, flex: 'none', width: 'auto'}}
                  value={newChoreRoom}
                  onChange={e => setNewChoreRoom(e.target.value)}
                >
                  {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select
                  style={{...styles.input, flex: 'none', width: 'auto'}}
                  value={newChoreFreq}
                  onChange={e => setNewChoreFreq(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
                <select
                  style={{...styles.input, flex: 'none', width: 'auto'}}
                  value={newChoreAssign}
                  onChange={e => setNewChoreAssign(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {roster.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <button style={{...styles.addBtn, background:'var(--accent2)', color:'#0d0f14', flexShrink:0}} onClick={handleAddChore}>Add</button>
              </div>
              {choreError && <p style={styles.error}>{choreError}</p>}

              {/* Chores list grouped by room */}
              {ROOMS.map(room => {
                const roomChores = chores.filter(c => c.room === room);
                if (!roomChores.length) return null;
                return (
                  <div key={room}>
                    <p style={{fontFamily:'var(--font-mono)', fontSize:'0.62rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--text-dimmer)', margin:'10px 0 6px'}}>{room}</p>
                    {roomChores.map(chore => (
                      <div key={chore.id} style={{display:'flex', alignItems:'center', gap:8, padding:'7px 10px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', marginBottom:4}}>
                        <span style={{flex:1, fontSize:'0.88rem', color:'var(--text)'}}>{chore.name}</span>
                        <span style={{fontFamily:'var(--font-mono)', fontSize:'0.6rem', color:'var(--text-dimmer)', textTransform:'uppercase'}}>{chore.frequency}</span>
                        <select
                          style={{fontFamily:'var(--font-mono)', fontSize:'0.68rem', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', padding:'3px 6px'}}
                          value={chore.assigned_to || ''}
                          onChange={e => handleUpdateChoreAssign(chore.id, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {roster.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                        <button style={{...styles.deleteBtn, padding:'3px 8px'}} onClick={() => handleDeleteChore(chore.id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                );
              })}
              {chores.length === 0 && <p style={styles.empty}>No chores yet</p>}
            </section>
          )}

          {/* Roster */}
          {activeSection === 'roster' && (
            <section style={styles.section}>
              <p style={styles.hint}>People who cook meals. Tap a name on the meal planner to cycle through the roster. Star = default cook.</p>
              <div style={styles.addRow}>
                <input
                  style={styles.input}
                  value={newMemberName}
                  onChange={e => { setNewMemberName(e.target.value); setRosterError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAddMember()}
                  placeholder="Name (e.g. Esteban)"
                />
                <button style={{...styles.addBtn, background: newMemberColor, color: '#0d0f14', flexShrink: 0}} onClick={handleAddMember}>Add</button>
              </div>
              <div style={rs.swatchRow}>
                {COLOR_PALETTE.map(c => (
                  <button
                    key={c}
                    onClick={() => setNewMemberColor(c)}
                    style={{
                      ...rs.swatch,
                      background: c,
                      boxShadow: newMemberColor === c ? `0 0 0 2px var(--bg), 0 0 0 4px ${c}` : 'none',
                    }}
                  />
                ))}
              </div>
              {rosterError && <p style={styles.error}>{rosterError}</p>}
              <div style={styles.rosterList}>
                {roster.map(member => (
                  <React.Fragment key={member.id}>
                  <div style={styles.rosterRow}>
                    <div style={{
                      ...styles.colorSwatch,
                      background: member.color + '33',
                      border: `2px solid ${member.color}`,
                    }}>
                      <span style={{fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: member.color, fontWeight: 600}}>
                        {member.name.slice(0,2).toUpperCase()}
                      </span>
                    </div>
                    <span style={styles.rosterName}>{member.name}</span>
                    {member.is_default ? (
                      <span style={styles.defaultBadge}>★ Default</span>
                    ) : (
                      <button style={styles.setDefaultBtn} onClick={() => handleSetDefault(member.id)}>Set default</button>
                    )}
                    <button style={styles.chipDelete} onClick={() => handleDeleteMember(member.id)}>×</button>
                  </div>
                  <div style={rs.swatchRow}>
                    {COLOR_PALETTE.map(c => (
                      <button
                        key={c}
                        onClick={async () => {
                          await updateRosterMember(member.id, { name: member.name, color: c });
                          setRoster(prev => prev.map(m => m.id === member.id ? {...m, color: c} : m));
                        }}
                        style={{
                          ...rs.swatch,
                          background: c,
                          width: 16, height: 16,
                          boxShadow: member.color === c ? `0 0 0 2px var(--bg), 0 0 0 3px ${c}` : 'none',
                        }}
                      />
                    ))}
                  </div>
                  </React.Fragment>
                ))}
                {roster.length === 0 && <p style={styles.empty}>No one added yet</p>}
              </div>
            </section>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 100,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  sheet: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderBottom: 'none',
    borderRadius: '16px 16px 0 0',
    width: '100%', maxWidth: 680,
    maxHeight: '88vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  sheetHeader: {
    display: 'flex', alignItems: 'center',
    padding: '16px 20px 12px',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0, gap: 12,
  },
  sheetTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.3rem', color: 'var(--accent)', fontStyle: 'italic',
    flexShrink: 0,
  },
  sectionTabs: {
    display: 'flex', gap: 4, flex: 1,
  },
  sectionTab: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
    background: 'transparent', border: '1px solid transparent',
    borderRadius: 20, padding: '3px 12px', cursor: 'pointer',
  },
  sectionTabActive: {
    color: 'var(--accent)', border: '1px solid var(--accent)',
    background: 'rgba(232,201,122,0.08)',
  },
  closeBtn: {
    background: 'none', border: 'none',
    color: 'var(--text-dim)', fontSize: '1.1rem', cursor: 'pointer', padding: '4px 8px',
  },
  body: {
    overflowY: 'auto', flex: 1, padding: '16px 20px 24px',
  },
  section: {
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  addRow: { display: 'flex', gap: 8 },
  input: {
    flex: 1,
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text)',
    fontSize: '0.9rem', padding: '9px 12px', outline: 'none',
    fontFamily: 'var(--font-body)',
  },
  addBtn: {
    background: 'var(--accent)', color: '#0d0f14', border: 'none',
    borderRadius: 'var(--radius-sm)', padding: '9px 16px',
    fontFamily: 'var(--font-mono)', fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer', flexShrink: 0,
  },
  error: {
    fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--danger)', marginTop: -4,
  },
  chipGrid: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  chip: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '5px 8px 5px 11px',
    background: 'var(--surface2)', border: '1px solid rgba(232,201,122,0.2)', borderRadius: 20,
  },
  chipName: { fontSize: '0.85rem', color: 'var(--text)' },
  chipDelete: {
    background: 'none', border: 'none', color: 'var(--text-dimmer)',
    fontSize: '1rem', cursor: 'pointer', lineHeight: 1, padding: '0 2px',
  },
  empty: {
    fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
    color: 'var(--text-dimmer)', fontStyle: 'italic',
  },
  hint: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    color: 'var(--text-dimmer)', lineHeight: 1.5, marginBottom: 4,
  },
  rosterList: { display: 'flex', flexDirection: 'column', gap: 8 },
  rosterRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px',
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
  },
  colorSwatch: {
    width: 30, height: 30, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  rosterName: {
    fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--text)', flex: 1,
  },
  defaultBadge: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    color: 'var(--accent)', letterSpacing: '0.05em',
  },
  swatchRow: {
    display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4,
  },
  swatch: {
    width: 20, height: 20, borderRadius: '50%',
    border: 'none', cursor: 'pointer', flexShrink: 0,
    transition: 'box-shadow 0.15s',
  },
  setDefaultBtn: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    color: 'var(--text-dimmer)', background: 'none',
    border: '1px solid var(--border)', borderRadius: 20,
    padding: '2px 8px', cursor: 'pointer',
  },
};

// Pet-specific styles
const ps = {
  petList: { display: 'flex', flexDirection: 'column', gap: 12 },
  petRow: {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  petHeader: {},
  petInfo: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  petName: { fontFamily: 'var(--font-body)', fontSize: '0.95rem', color: 'var(--text)', fontWeight: 500, flex: 1 },
  petMeta: { fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-dim)' },
  petActions: { display: 'flex', gap: 6 },
  editFields: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  input: {
    flex: 1, minWidth: 120,
    background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text)',
    fontSize: '0.85rem', padding: '6px 10px', outline: 'none',
    fontFamily: 'var(--font-body)',
  },
  dogLabel: { display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', flexShrink: 0 },
  dogText: { fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-dim)' },
  saveBtn: {
    background: 'var(--accent)', color: '#0d0f14', border: 'none',
    borderRadius: 'var(--radius-sm)', padding: '5px 12px',
    fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  },
  cancelBtn: {
    background: 'none', border: '1px solid var(--border)', color: 'var(--text-dim)',
    borderRadius: 'var(--radius-sm)', padding: '5px 10px',
    fontFamily: 'var(--font-mono)', fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0,
  },
  editBtn: {
    background: 'none', border: '1px solid var(--border)', color: 'var(--text-dim)',
    borderRadius: 'var(--radius-sm)', padding: '3px 9px',
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem', cursor: 'pointer',
  },
  deleteBtn: {
    background: 'rgba(232,122,122,0.1)', border: '1px solid rgba(232,122,122,0.3)', color: '#e87a7a',
    borderRadius: 'var(--radius-sm)', padding: '3px 9px',
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem', cursor: 'pointer',
  },
  medsSection: { display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6, borderTop: '1px solid var(--border)' },
  medsLabel: { fontFamily: 'var(--font-mono)', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-dimmer)' },
  medRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  medName: { fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--text)', flex: 1 },
  medQty: { fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-dim)', flexShrink: 0 },
  addMedRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  addMedBtn: {
    background: 'rgba(232,122,184,0.12)', border: '1px solid rgba(232,122,184,0.35)', color: '#e87ab8',
    borderRadius: 'var(--radius-sm)', padding: '6px 12px',
    fontFamily: 'var(--font-mono)', fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0,
  },
  addPetRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 },
};

const rs = {
  swatchRow: {
    display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 2, marginTop: 4,
  },
  swatch: {
    width: 20, height: 20, borderRadius: '50%',
    border: 'none', cursor: 'pointer', flexShrink: 0,
    transition: 'box-shadow 0.15s',
    padding: 0,
  },
};
